import React from 'react';
import { observer } from 'mobx-react';
import { withNamespaces, Trans } from 'react-i18next';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { Link, routerStore } from 'core/routing';
import FormActionButton from 'libs/forms/views/formActionButton';
import { TextField } from 'libs/forms/views/fieldTypes/text';
import { TextAreaField } from 'libs/forms/views/fieldTypes/textArea';
import { FileField } from 'libs/forms/views/fieldTypes/file';
import { analytics } from 'core/analytics';

import { MainWrapper, BaseEditField } from 'legacyComponents/styled/lib';
import { Avatar } from 'apps/components/shared';
import { Button, Tabs, Tab } from 'components';
import { Cropper } from 'apps/components/cropper';
import { ButtonGroup } from 'legacyComponents/styles';

import { media } from 'legacyComponents/styled/styleUtils';

import { AvatarInputWrapper } from 'apps/profile/views/overview.styles';
import { SocialStats } from 'legacyComponents/layout/socialStats';
import { Follow } from 'apps/content/views/follow';
import { conf as mainConf } from 'apps/config.yaml';
import { imageSrc } from 'apps/file/utils';
import { Overview, Folder, Stories, Forum } from 'icons';

const Wrapper = styled.div`
  padding: ${props => props.theme.spacing.default} 0;
`;

const MainProfileAvatar = styled(Avatar)`
  width: 100px;
  height: 100px;
  box-shadow: none;
  display: inline-block;
  border: 1px solid ${props => props.theme.color.wfGrey100};
  vertical-align: top;
  ${media.s`
    width: 70px;
    height: 70px;
  `};
  ${media.xs`
    width: 60px;
    height: 60px;
  `};
`;

const SmallProfileAvatar = styled(MainProfileAvatar)`
  width: 60px;
  height: 60px;
  &:hover {
    opacity: 0.8;
  }
`;

const AvatarInput = styled(AvatarInputWrapper)`
  width: 100px;
  height: 100px;
  display: inline-block;
  position: relative;
  > div {
    border-radius: 100%;
  }
  & .cropper {
    border-radius: 100%;
    overflow: hidden;
    -webkit-mask-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAIAAACQd1PeAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAA5JREFUeNpiYGBgAAgwAAAEAAGbA+oJAAAAAElFTkSuQmCC);
    z-index: 14;
    width: 100px;
    height: 100px;
    margin-bottom: 6px;
  }
`;

const DetailsWrapper = styled.div`
  display: inline-block;
  vertical-align: top;
  margin-left: 15px;
  width: calc(100% - 117px);
  ${media.s`
    width: calc(100% - 87px);
  `};
  ${media.xs`
    margin-left: 0;
    width: calc(100%);
  `};
`;

export const Title = styled.h1`
  font-size: 1.5em;
  margin: 10px 0 0 0;
  ${media.s`
    font-size: 1.3em;
  `};
`;

export const TitleInput = styled(BaseEditField)`
  position: relative;
  width: 320px;
  font-size: 1.5em;
  margin: 15px 0 4px 0;
  display: block;
  border: ${props =>
    props.focussed
      ? `1px solid ${props.theme.color.primary}`
      : '1px solid #cacaca'};
  > input {
    font-weight: 600;
    width: 100%;
  }
  ::placeholder {
    font-weight: 600;
  }
  ${media.s`
    font-size: 1.3em;
  `};
`;

const Slug = styled(Link)`
  font-weight: 600;
  color: ${props => props.theme.color.primary};
  font-size: 0.8em;
  &:hover {
    text-decoration: underline;
  }
`;

export const Description = styled.p`
  font-size: 1em;
  width: 620px;
  line-height: 1.4;
  margin: 10px 0 0 0;
  text-align: ${props => (props.center ? 'center' : 'left')};
  ${media.m`
    width: 560px;
  `};
  ${media.s`
    width: 445px;
  `};
  ${media.xs`
    width: auto;
  `};
`;

export const DescriptionInput = styled(BaseEditField)`
  position: relative;
  width: 640px;
  font-size: 1em;
  line-height: 1.4;
  border: ${props =>
    props.focussed
      ? `1px solid ${props.theme.color.primary}`
      : '1px solid #cacaca'};
  margin: 8px 0 6px 0;
  display: block;
  text-align: center;
  > textarea {
    width: 100%;
    min-height: inherit;
    line-height: 1.4;
  }
`;

const ButtonsWrapper = styled.div`
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.default};
`;

const EditButton = styled(Button)`
  ${media.s`
    display: none;
  `};
`;

const FormActionButtonStyled = styled(FormActionButton)`
  margin-top: 0;
`;

@withNamespaces()
@observer
export class InitiativeIntro extends React.Component {
  render() {
    const {
      initiativeManager: { initiative, pendingInvitation },
      route,
    } = this.props;
    const { canUpdate, forum, content, hasServices } = initiative;
    const { AcceptInvitation, RejectInvitation } = initiative;

    const routeName = route.name;
    const routeParams = route.params;

    const tabs = (
      <MainWrapper>
        <Tabs
          value={routeName}
          onClick={(event, value) => {
            routerStore.router.navigate(value, {
              ...routeParams,
            });
          }}
        >
          <Tab value='initiative' icon={<Overview />}>
            <Trans>Overview</Trans>
          </Tab>
          <Tab
            value='initiative.projects'
            data-intercom-target='organisation-projects'
            icon={<Folder />}
          >
            <Trans>Projects</Trans>
            {/* <NotifCount active={routeName.endsWith('.projects')}>
              {initiative.projectsCount}
            </NotifCount> */}
          </Tab>
          <Tab
            value='initiative.stories'
            data-intercom-target='organisation-stories'
            icon={<Stories />}
          >
            <Trans>Stories</Trans>
            {/* <NotifCount active={routeName.endsWith('.stories')}>
              {initiative.storiesCount}
            </NotifCount> */}
          </Tab>
          {(forum || canUpdate) && (
            <Tab
              value='initiative.forum'
              data-intercom-target='organisation-forum'
              icon={<Forum />}
            >
              <Trans>Forum</Trans>
            </Tab>
          )}
          {hasServices && (
            <Tab value='initiative.services'>
              <Trans>Services</Trans>
            </Tab>
          )}
        </Tabs>
      </MainWrapper>
    );

    const {
      avatar,
      imageFallbackPath,
      title,
      slug,
      description,
      editing,
      editFormStore,
      Edit,
      Cancel,
      Save,
    } = initiative;

    let button = <Follow content={content} size='small' />;
    if (pendingInvitation) {
      button = (
        <ButtonGroup>
          <Button
            text={<Trans>Accept Invite</Trans>}
            onClick={() => AcceptInvitation(pendingInvitation)}
            size='small'
          />
          <Button
            text={<Trans>Reject Invite</Trans>}
            onClick={() => RejectInvitation(pendingInvitation)}
            variant='destructive'
            size='small'
          />
        </ButtonGroup>
      );
    }

    if (route.name.includes('.') && !route.name.endsWith('services')) {
      return (
        <>
          <Wrapper>
            <MainWrapper>
              <Link name='initiative' params={routeParams} content={content}>
                <SmallProfileAvatar
                  noOpacity={true}
                  src={imageSrc(
                    avatar,
                    imageFallbackPath,
                    mainConf.imgSizes.profile.large,
                  )}
                />
              </Link>
              <DetailsWrapper>
                <Title>{title}</Title>
                <Slug name='initiative' params={routeParams} content={content}>
                  {slug}
                </Slug>
              </DetailsWrapper>
              {canUpdate ? null : <ButtonsWrapper>{button}</ButtonsWrapper>}
            </MainWrapper>
          </Wrapper>
          {tabs}
        </>
      );
    }

    let settingsButton = null;
    if (canUpdate) {
      if (editing) {
        button = (
          <>
            <FormActionButtonStyled
              formStore={editFormStore}
              buttonHandler={Cancel}
              buttonText='Cancel'
              variant='secondary'
              size='small'
              outline
            />
            <FormActionButtonStyled
              formStore={editFormStore}
              buttonHandler={Save}
              buttonText='Save'
              buttonTextPosting='Saving...'
              size='small'
            />
          </>
        );
      } else {
        button = (
          <EditButton onClick={Edit} text={<Trans>Edit</Trans>} size='small' />
        );
      }

      settingsButton = (
        <Link
          onClick={() => {
            analytics.sendEvent(
              'clicked-settings-initiative',
              {},
              { ga: true },
            );
          }}
          name='workspace.spaceSettings.teams'
          params={{ spacePath: initiative.slug }}
        >
          <Button
            icon='settings'
            ariaLabel='Personal settings'
            data-tip='Personal settings'
            data-for='tooltipSettings'
            outline
            size='small'
          />
        </Link>
      );
    }

    return (
      <>
        <Wrapper data-intercom-target='organisation-intro'>
          <MainWrapper>
            {editing ? (
              <>
                <FileField
                  editComponent={AvatarInput}
                  previewComponent={Cropper}
                  fieldStore={
                    editFormStore && editFormStore.fieldStores.get('avatar')
                  }
                />
                <DetailsWrapper>
                  <TextField
                    fieldStore={
                      editFormStore && editFormStore.fieldStores.get('title')
                    }
                    editComponent={TitleInput}
                    validationPosition={{ top: '5px', right: '-25px' }}
                  />
                  <Slug
                    name='initiative'
                    params={routeParams}
                    content={content}
                  >
                    {slug}
                  </Slug>
                  <TextAreaField
                    fieldStore={
                      editFormStore &&
                      editFormStore.fieldStores.get('description')
                    }
                    editComponent={DescriptionInput}
                    validationPosition={{ top: '2px', right: '-25px' }}
                    className='lightBorder'
                  />
                  <SocialStats app={initiative} />
                  <ButtonsWrapper>{button}</ButtonsWrapper>
                </DetailsWrapper>
              </>
            ) : (
              <>
                <MainProfileAvatar
                  noOpacity={true}
                  src={imageSrc(
                    avatar,
                    imageFallbackPath,
                    mainConf.imgSizes.profile.large,
                  )}
                />
                <DetailsWrapper>
                  <Title>{title}</Title>
                  <Slug
                    name='initiative'
                    params={routeParams}
                    content={content}
                  >
                    {slug}
                  </Slug>
                  <Description>{description}</Description>
                  <SocialStats app={initiative} />
                  <ButtonsWrapper>
                    {button}
                    {settingsButton}
                  </ButtonsWrapper>
                </DetailsWrapper>
              </>
            )}
          </MainWrapper>
        </Wrapper>
        {tabs}
      </>
    );
  }
}

InitiativeIntro.propTypes = {
  initiativeManager: PropTypes.object,
  route: PropTypes.object,
  t: PropTypes.func,
};
